/**
 * URL ↔ content path utilities.
 *
 * Since docs content lives at content/{locale}/docs/{site}/..., content paths
 * and URLs are identical — no translation needed. These functions exist for
 * extracting URL segments accounting for the /docs/ position.
 */

/**
 * Default site locale. Mirrors `siteMeta.defaultLocale` in `nuxt.config.ts`
 * (which becomes `runtimeConfig.public.site.defaultLocale`). Kept as a
 * standalone constant so route helpers like `localeFromUrl()` can read it
 * without a Vue/Nuxt context — they run in places (error.vue at boot, route
 * guards before hydration) where `useRuntimeConfig()` may not be live yet.
 *
 * If this changes, also update `nuxt.config.ts:siteMeta.defaultLocale`.
 */
export const DEFAULT_LOCALE = 'en'

const DOCS_SEGMENT = 'docs'

/**
 * Identity since the URL/content-path unification — the function is retained
 * as a semantic marker so call sites read as "I'm crossing the URL→content
 * boundary," and a future re-introduction of a real translation has one
 * place to land. Don't inline the call.
 */
export function contentPathToUrl(path: string): string {
  return path
}

/** Identity. See `contentPathToUrl` — same retention rationale. */
export function urlToContentPath(path: string): string {
  return path
}

/**
 * Extract locale from a URL path (segment[1]). Falls back to `DEFAULT_LOCALE`
 * (currently 'en') when the path has no locale segment — e.g. bare `/`,
 * which Nuxt redirects fast but may briefly resolve through here during
 * navigation or error rendering. Pass an explicit `fallback` to override.
 */
export function localeFromUrl(path: string, fallback: string = DEFAULT_LOCALE): string {
  return path.split('/')[1] || fallback
}

/** Extract site slug from a URL path, accounting for /docs/ offset. */
export function siteFromUrl(path: string): string {
  const segments = path.split('/')
  if (segments[2] === DOCS_SEGMENT) return segments[3] || ''
  return segments[2] || ''
}

/** Extract subsite slug from a URL path, accounting for /docs/ offset. */
export function subsiteFromUrl(path: string): string {
  const segments = path.split('/')
  if (segments[2] === DOCS_SEGMENT) return segments[4] ?? ''
  return segments[3] ?? ''
}

/** True when the URL contains the /docs/ segment at position 2. */
export function isDocsUrl(path: string): boolean {
  return path.split('/')[2] === DOCS_SEGMENT
}

/**
 * Frontmatter `redirect:` values are absolute paths — verified across every
 * docs stub: 975 target `/{locale}/docs/...` and the rest target a top-level
 * route like `/{locale}/validator`. So this is a passthrough.
 *
 * It previously inserted `/docs/` at position 2 for *legacy* docs-relative
 * targets that omitted it (e.g. `/nl/gebruiken/platform`). The slug-convergence
 * rewrote those to absolute paths, so none remain — but the injection also
 * fired on top-level targets, corrupting `/{locale}/validator` into the ghost
 * `/{locale}/docs/validator` (404), which is what surfaced in search for the
 * validator redirect stubs. Treat the authored path as canonical.
 */
export function normalizeDocsRedirect(path: string): string {
  return path
}
